import React, { useContext, useState, useEffect } from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import v from 'scss/v';
import { StyledInput, I18N, GlobalContext, I18NContext, PhoneNumberInputIntl } from 'commons';
import * as currentUserSelectors from 'state/selectors/currentUserSelectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretSquareUp, faCaretSquareDown, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Space, Collapse } from '@eb/antd';
import { convertHTMLTags } from 'utils';
import FieldErrorMessage from '../FieldErrorMessage';
import PinExtInputPagerService from './PinExtInputPagerService';
import QuietTimeTag from './QuietTimeTag';
import QuietTimeForm from './QuietTimeForm';
import { useTimeZoneModal } from './TimeZoneSection';


const { Panel } = Collapse;

// Helper for text-based fields
function handleChange(contactPath, value, isReceiveNotifManuallyUnchecked, onChange) {
  onChange({
    ...contactPath,
    value,
    ...(isReceiveNotifManuallyUnchecked ? {} : { pathEnable: !!value })
  });
}

// Helper for notification checkbox
function handleNotificationChange(contactPath, checked, setIsReceiveNotifManuallyUnchecked, onChange) {
  setIsReceiveNotifManuallyUnchecked(!checked);
  onChange({
    ...contactPath,
    pathEnable: checked
  });
}

function handlePhoneChange(contactPath, phoneValue, isReceiveNotifManuallyUnchecked, onChange, meta = {}) {
  onChange({
    ...contactPath,
    ...phoneValue,
    ...(
      (!isReceiveNotifManuallyUnchecked && !meta.fromUseAsIs)
        ? { pathEnable: !!phoneValue.value }
        : {}
    )
  });
}

const renderQuietTimeTags = (pathId, quietTimeFrames, timeZone, onDelete, handleQuietTimeSubmitForEdit) => quietTimeFrames.map((item, quietTimeIndex) => (
  <QuietTimeTag
    pathId={pathId}
    id={quietTimeIndex}
    quietTime={item}
    onDeleteTag={() => onDelete(quietTimeIndex)}
    timeZone={timeZone}
    handleQuietTimeSubmitForEdit={handleQuietTimeSubmitForEdit}
  />
));

const useQuietTimeTags = (contactPath, pathId, quietTimes, timeZone, onChange, handleQuietTimeSubmitForEdit) => {
  const [shouldUpdateTags, setShouldUpdateTags] = useState(false);
  const [quietTimeFrames, updateQuietTimeFrames] = useState(quietTimes || []);

  const updateState = (shouldUpdate, newFrames) => {
    setShouldUpdateTags(shouldUpdate);
    if (newFrames) {
      updateQuietTimeFrames(newFrames);
      onChange({ quietTimeFrames: newFrames });
    }
  };

  const handleOnQuietTimeTagsChange = (quietTimeIndex) => {
    quietTimeFrames.splice(quietTimeIndex, 1);
    setShouldUpdateTags(true);
    updateQuietTimeFrames(quietTimeFrames);
    onChange({ quietTimeFrames });
  };

  const [quietTimeTags, setQuietTimeTags] = useState(() => {
    if (quietTimeFrames) {
      return renderQuietTimeTags(pathId, quietTimeFrames, timeZone, handleOnQuietTimeTagsChange, handleQuietTimeSubmitForEdit);
    }
    return [];
  });

  useEffect(() => {
    if (shouldUpdateTags) {
      const tags = renderQuietTimeTags(pathId, quietTimeFrames, timeZone, handleOnQuietTimeTagsChange, handleQuietTimeSubmitForEdit);
      setQuietTimeTags(tags);
      setShouldUpdateTags(false);
    }
  }, [shouldUpdateTags]);

  useEffect(() => {
    const tags = renderQuietTimeTags(pathId, quietTimeFrames, timeZone, handleOnQuietTimeTagsChange, handleQuietTimeSubmitForEdit);
    setQuietTimeTags(tags);
  }, [quietTimeFrames.length, timeZone, contactPath]);

  return [quietTimeTags, updateState];
};

const QuietTimeSection = ({ children, handleQuietTimeSubmitForAddNew, timeZone, toggleTimeZoneModal }) => {
  const i18n = useContext(I18NContext);
  const [openAddQuietTime, setOpenAddQuietTime] = useState(false);
  const getCollapseIcon = (isActive) => (
    <span>
      <FontAwesomeIcon
        icon={isActive ? faChevronDown : faChevronRight}
        width="0.875em"
        height="1em"
      />
    </span>
  );
  return (
    <div className="mt-1">
      <Space direction="vertical" className="col-12 px-0">
        {children}
        {
          openAddQuietTime &&
          <Collapse
            defaultActiveKey={['add-quiet-time']}
            collapsible="disabled"
            className="rounded-0"
            expandIcon={({ isActive }) => getCollapseIcon(isActive)}
          >
            <Panel header={i18n['ssp.myprofile.add.quietTime']} key="add-quiet-time" className="rounded-0">
              <QuietTimeForm
                timeZone={timeZone}
                autoFocus
                onSubmit={(updatedQuietTime) => {
                  handleQuietTimeSubmitForAddNew(updatedQuietTime, () => { setOpenAddQuietTime(false); });
                }}
                onClickCancel={() => { setOpenAddQuietTime(false); }}
              />
            </Panel>
          </Collapse>
        }
      </Space>
      {
        !openAddQuietTime &&
        <div className="mt-3">
          <a
            href="JavaScript:void(0);"
            onClick={() => {
              if (!timeZone) {
                toggleTimeZoneModal(true);
              } else {
                setOpenAddQuietTime(true);
              }
            }}
            eb-test="ssp_profile_link_add_quiet_time"
          >
            <I18N>ssp.myprofile.add.quietTime</I18N>
          </a>
        </div>
      }
    </div>
  );
};

QuietTimeSection.propTypes = {
  children: PropTypes.element.isRequired,
  handleQuietTimeSubmitForAddNew: PropTypes.func,
  timeZone: PropTypes.string.isRequired,
  toggleTimeZoneModal: PropTypes.func.isRequired,
};

QuietTimeSection.defaultProps = {
  handleQuietTimeSubmitForAddNew: () => {},
};

const useHandleQuietTimeTags = (contactPath, timeZone, onChange) => {
  const { pathId, name, quietTimeFrames } = contactPath;
  const [quietTimeTags, updateState] = useQuietTimeTags(contactPath, pathId, quietTimeFrames, timeZone, onChange, handleQuietTimeSubmitForEdit);

  function handleQuietTimeSubmitForEdit(id, quietTimeFrame, callback) {
    contactPath.quietTimeFrames.splice(id, 1, quietTimeFrame);
    updateState(true, contactPath.quietTimeFrames);
    if (callback) {
      callback();
    }
  }

  function handleQuietTimeSubmitForAddNew(quietTimeFrame, callback) {
    if (!contactPath.quietTimeFrames) {
      contactPath.quietTimeFrames = [];
    }
    contactPath.quietTimeFrames.push(quietTimeFrame);
    updateState(true, contactPath.quietTimeFrames);
    if (callback) {
      callback();
    }
  }

  return [quietTimeTags, handleQuietTimeSubmitForAddNew, handleQuietTimeSubmitForEdit];
};

const renderTextField = (
  contactPath,
  onChange,
  contactPathError,
  setContactPathError,
  touched,
  setFieldTouched,
  index,
  pageServices,
  isNeedValidation,
  setIsNeedValidation,
  orderMemberPath,
  toggleTimeZoneModal,
  quietTimeTags,
  timeZone,
  useDeviceTimeFrame,
  isAllFieldMandatory,
  handleQuietTimeSubmitForAddNew,
  isReceiveNotifManuallyUnchecked, 
  setIsReceiveNotifManuallyUnchecked 
) => {
  const { isInLoggedInEditProfileMode } = useContext(GlobalContext);

  const { pathId, value, name, mandatory, editable, pathEnable } = contactPath;

  const inputName = `contactPath--${pathId}`;
  const hasTouched = _.has(touched, inputName);
  const hasError = Boolean(contactPathError[inputName]);
  const fieldMandatory = isAllFieldMandatory || mandatory;

  return (
    <div className={classnames([(orderMemberPath) ? 'col-md-11' : 'col-md-12'])}>
      <div className="col-12 px-0 d-inline-block">
        <label id={`${pathId}Label`} className="col-form-label col-12 col-lg-6 px-0" htmlFor={inputName}>

          {name}
          {
            fieldMandatory &&
            <span aria-hidden="true" className="required-star">*</span>
          }
          {
            fieldMandatory &&
            <span className="sr-only"><I18N>information.label.required</I18N></span>
          }
        </label>
        {
          isInLoggedInEditProfileMode && useDeviceTimeFrame &&
          <div className="pull-right">
            <input
              id={`${pathId}_receive_notification`}
              type="checkbox"
              checked={pathEnable}
              onChange={(event) => {
              const checked = event.target?.checked;
              // --- Track manual uncheck of Receive Notification ---
              handleNotificationChange(contactPath, checked, setIsReceiveNotifManuallyUnchecked, onChange);
              }}
            />
            <label className="mr-2" htmlFor={`${pathId}_receive_notification`}>
              <span className="sr-only">{`${name} `}</span>
              <I18N>ssp.myprofile.receive.notification</I18N>
            </label>
          </div>
        }
      </div>
      <StyledInput
        autoComplete="off"
        data-index={index}
        disabled={!editable}
        type="email"
        className={editable ? 'form-control js_blur' : 'form-control'}
        id={inputName}
        name={inputName}
        maxLength={80}
        minLength={4}
        value={value || ''}
        hasError={hasError}
        style={{ marginLeft: 0 }}
        aria-invalid={hasTouched && hasError}
        aria-labelledby={`${pathId}Label${(hasTouched && hasError) ? ` ${pathId}ErrorDesc` : ''}`}
        onChange={e => {          
          setIsNeedValidation(false);
          const { value } = e.target;
          // --- Only enable path if not manually unchecked ---
           handleChange(contactPath, value, isReceiveNotifManuallyUnchecked, onChange);      
        }}
        onBlur={e => {
          const { value } = e.target;
          // need to validate email inputed
          let errorMsg;
          if (fieldMandatory && !value) {
            errorMsg = `jquery.validation.field.required, ${convertHTMLTags(name)}`;
            setContactPathError(inputName, errorMsg);
          } else if (value) {
            // if user enters need to check
            const reg = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
            if (!reg.test(value)) {
              errorMsg = 'jquery.validation.email';
              setContactPathError(inputName, errorMsg);
            } else {
              setContactPathError(inputName, 'delete');
            }
          } else {
            // delete operation
            setContactPathError(inputName, 'delete');
          }
          setFieldTouched(inputName, true);
          setIsNeedValidation(false);
          const { nativeEvent } = e;
          if (nativeEvent && nativeEvent.formSubmit) {
            const { formSubmit } = nativeEvent;
            formSubmit[inputName] = true;
            const { validationCtrl: { size, resolver } } = nativeEvent;
            const { length } = Object.keys(formSubmit);
            if (length === size) {
              resolver();
            }
          }
        }}
      />
      {hasTouched && hasError && (
        <FieldErrorMessage id={`${pathId}ErrorDesc`} aria-live="assertive">
          <I18N>{contactPathError[inputName]}</I18N>
        </FieldErrorMessage>
      )}
      <PinExtInputPagerService
        pageServices={pageServices}
        onChange={onChange}
        contactPath={contactPath}
        setIsNeedValidation={setIsNeedValidation}
        setFieldTouched={setFieldTouched}
        setContactPathError={setContactPathError}
        contactPathError={contactPathError}
        touched={touched}
        mandatory={fieldMandatory}
      />
      {
        isInLoggedInEditProfileMode && pathEnable && useDeviceTimeFrame &&
        <QuietTimeSection
          timeZone={timeZone}
          handleQuietTimeSubmitForAddNew={handleQuietTimeSubmitForAddNew}
          toggleTimeZoneModal={toggleTimeZoneModal}
        >
          {quietTimeTags}
        </QuietTimeSection>
      }
    </div>
  );
};

const renderPhoneField = (
  values,
  contactPath,
  onChange,
  errors,
  setFieldError,
  touched,
  setFieldTouched,
  setFieldValue,
  contactPathError,
  setContactPathError,
  index,
  pageServices,
  isNeedValidation,
  setIsNeedValidation,
  orderMemberPath,
  toggleTimeZoneModal,
  quietTimeTags,
  timeZone,
  useDeviceTimeFrame,
  isAllFieldMandatory,
  setIscallOutMsgShow,
  contactPathSelectedCountries,
  handleQuietTimeSubmitForAddNew,
  isReceiveNotifManuallyUnchecked, 
  setIsReceiveNotifManuallyUnchecked 
) => {
  const {
    isInLoggedInEditProfileMode,
    organization: { isEnableCountryCodeForMemberPortal },
  } = useContext(GlobalContext);

  const {
    value,
    pathId,
    countryCode,
    name,
    editable,
    mandatory,
    noValidate,
    phoneExt,
    systemRequirement,
    pathEnable,
  } = contactPath;
  const { organization: { defaultCountry: orgCountry = 'US' } } = useContext(GlobalContext);
  const inputName = `contactPath--${pathId}`;
  const hasTouched = _.has(touched, inputName);
  const hasError = Boolean(contactPathError[inputName]);
  const fieldMandatory = isAllFieldMandatory || mandatory;
  const currentCountryCode = _.isNull(countryCode) ? orgCountry : countryCode;
  if (contactPath.type === 'SMS' &&
      contactPath.code !== 'ONEWAYSMS' &&
      currentCountryCode === 'US') {
    setIscallOutMsgShow(true);
  }

  const handleValidateCountry = iso2 => {
    if (!iso2) {
      setContactPathError(`${inputName}countryCode`, 'ssp.country.code.invalid');
    } else if (editable && isEnableCountryCodeForMemberPortal) {
      if (contactPathSelectedCountries.findIndex(item =>
        item.iso2.toLowerCase() === iso2.toLowerCase()) === -1) {
        setContactPathError(`${inputName}countryCode`, 'ssp.country.code.invalid');
      } else {
        setContactPathError(`${inputName}countryCode`, 'delete');
      }
    }
  };

  const checkCountryCode = () => {
    const paths = values.contactPaths.map(path => {
      if (path.type === 'SMS' && _.isNull(path.countryCode)) {
        path.countryCode = orgCountry;
      }
      return path;
    });
    if (paths.findIndex(path =>
      path.type === 'SMS' &&
      path.code !== 'ONEWAYSMS' &&
      path.pathId != pathId &&
      path.countryCode === 'US') === -1) {
      setIscallOutMsgShow(false);
    }
  }

  return (
    <div className={classnames([(orderMemberPath) ? 'col-md-11' : 'col-md-12'])}>
      <div className="col-12 px-0 col-form-label d-inline-block">
        <div className="d-inline-block label col-12 col-lg-6 px-0">
          {name}
          {
            fieldMandatory &&
            <span aria-hidden="true" className="required-star">*</span>
          }
          {
            fieldMandatory &&
            <span className="sr-only"><I18N>information.label.required</I18N></span>
          }
        </div>
        {
          isInLoggedInEditProfileMode && useDeviceTimeFrame &&
          <div className="d-inline-block pull-right">
            <input
              id={`${pathId}_receive_notification`}
              type="checkbox"
              checked={pathEnable}
              // --- Updated handler to track manual uncheck ---
              onChange={(event) => {
                const checked = event.target?.checked;
                handleNotificationChange(contactPath, checked, setIsReceiveNotifManuallyUnchecked, onChange);
              }}
            />
            <label className="mr-2" htmlFor={`${pathId}_receive_notification`}>
              <span className="sr-only">{`${name} `}</span>
              <I18N>ssp.myprofile.receive.notification</I18N>
            </label>
          </div>
        }
      </div>
      <div className="col-12 px-0">
        <PhoneNumberInputIntl
          isEnableCountryCodeForMemberPortal={isEnableCountryCodeForMemberPortal}
          setFieldError={setContactPathError}
          inputName={inputName}
          pathId={pathId}
          editable={editable}
          hasTouched={hasTouched}
          mandatory={fieldMandatory}
          errors={contactPathError}
          setFieldTouched={setFieldTouched}
          setFieldValue={setFieldValue}
          values={values}
          onChangeCallback={(phoneValue, meta = {}) => {   
          // --- Only enable path if not manually unchecked ---
          handlePhoneChange(contactPath, phoneValue, isReceiveNotifManuallyUnchecked, onChange, meta);
  }}
          onCountryChangeCallback={() => checkCountryCode()}
          itemProperty={contactPath}
          setIsNeedValidation={setIsNeedValidation}
          setIscallOutMsgShow={setIscallOutMsgShow}
          pathLabel={name}
        />
        <PinExtInputPagerService
          pageServices={pageServices}
          onChange={onChange}
          contactPath={contactPath}
          setIsNeedValidation={setIsNeedValidation}
          setFieldTouched={setFieldTouched}
          setContactPathError={setContactPathError}
          contactPathError={contactPathError}
          touched={touched}
          mandatory={fieldMandatory}
        />
      </div>
      {
        isInLoggedInEditProfileMode && pathEnable && useDeviceTimeFrame &&
        <QuietTimeSection
          timeZone={timeZone}
          handleQuietTimeSubmitForAddNew={handleQuietTimeSubmitForAddNew}
          toggleTimeZoneModal={toggleTimeZoneModal}
        >
          {quietTimeTags}
        </QuietTimeSection>
      }
    </div>
  );
};

const renderMobilePush = (
  contactPath,
  onChange,
  contactPathError,
  setContactPathError,
  touched,
  setFieldTouched,
  index,
  pageServices,
  isNeedValidation,
  setIsNeedValidation,
  orderMemberPath,
  toggleTimeZoneModal,
  quietTimeTags,
  timeZone,
  useDeviceTimeFrame,
  handleQuietTimeSubmitForAddNew
) => {
  const { isInLoggedInEditProfileMode } = useContext(GlobalContext);
  const i18n = useContext(I18NContext);

  const { pathId, name, pathEnable, value } = contactPath;

  return (
    <div className={classnames([(orderMemberPath) ? 'col-md-11' : 'col-md-12'])}>
      <div className="col-12 px-0 col-form-label d-inline-block">
        <div id={`${pathId}Label`} className="d-inline-block label col-12 col-lg-6 px-0">
          {name}
        </div>
        {
          isInLoggedInEditProfileMode && useDeviceTimeFrame && value === 'yes' &&
          <div className="d-inline-block pull-right">
            <input
              id={`${pathId}_receive_notification`}
              type="checkbox"
              checked={pathEnable}
              // --- Updated handler to track manual uncheck ---
            onChange={(event) => {
              const checked = event.target.checked;
              handleNotificationChange(contactPath, checked, setIsReceiveNotifManuallyUnchecked, onChange);
              }}
            />
            <label className="mr-2" htmlFor={`${pathId}_receive_notification`}>
              <span className="sr-only">{`${name} `}</span>
              <I18N>ssp.myprofile.receive.notification</I18N>
            </label>
          </div>
        }
      </div>
      <div className="col-12 pl-0">
        <div id="mobilePush">
          {(value === 'yes') ? i18n.get('ssp.main.login') : i18n.get('ssp.myprofile.not.log')}
        </div>
      </div>
      {
        isInLoggedInEditProfileMode && pathEnable && useDeviceTimeFrame && value === 'yes' &&
        <QuietTimeSection
          timeZone={timeZone}
          handleQuietTimeSubmitForAddNew={handleQuietTimeSubmitForAddNew}
          toggleTimeZoneModal={toggleTimeZoneModal}
        >
          {quietTimeTags}
        </QuietTimeSection>
      }
    </div>
  );
};

const ActionButtonsContainer = styled.div`
  margin-top: .5rem;
  position: relative;
  z-index: 1;
`;

const ContactPathsRow = ({
  contactPath,
  handleMoveUp,
  handleMoveDown,
  errors,
  values,
  setFieldError,
  touched,
  setFieldTouched,
  setFieldValue,
  contactPathError,
  setContactPathError,
  onChange,
  index,
  allContactPaths,
  pageServices,
  isNeedValidation,
  useDeviceTimeFrame,
  setIsNeedValidation,
  orderMemberPath,
  profileTimeZones,
  isAllFieldMandatory,
  setIscallOutMsgShow,
  contactPathSelectedCountries
}) => {
  const i18n = useContext(I18NContext);

  const { type, value } = contactPath;
  const inActiveUpButton = index === 0;
  const inActiveDownButton = allContactPaths.length === index + 1;
  // a local state to track manual uncheck for Receive Notification
  const [isReceiveNotifManuallyUnchecked, setIsReceiveNotifManuallyUnchecked] = useState(false);


  const { timeZone } = values;

  const [quietTimeTags, handleQuietTimeSubmitForAddNew, handleQuietTimeSubmitForEdit] = useHandleQuietTimeTags(contactPath, timeZone, onChange);
  const onTimeZoneSave = (newValue) => {
    setFieldValue('timeZone', newValue);
  };

  const isMobilePushLoggedIn = (contactPathType, contactPathValue) => {
    if (contactPathType !== 'RECIP') {
      return true;
    }

    if (contactPathValue === 'yes') {
      return true;
    }

    return false;
  };

  const [timeZoneModal, toggleTimeZoneModal] = useTimeZoneModal(false, timeZone, null, onTimeZoneSave, false, profileTimeZones);
  let content;
  switch (type) {
    case 'SMS':
    case 'PHONE':
    case 'PAGER':
    case 'FAX':
    case 'TA':
    case 'TTYTDD':
      content = renderPhoneField(
        values,
        contactPath,
        onChange,
        errors,
        setFieldError,
        touched,
        setFieldTouched,
        setFieldValue,
        contactPathError,
        setContactPathError,
        index,
        pageServices,
        isNeedValidation,
        setIsNeedValidation,
        orderMemberPath,
        toggleTimeZoneModal,
        quietTimeTags,
        timeZone,
        useDeviceTimeFrame,
        isAllFieldMandatory,
        setIscallOutMsgShow,
        contactPathSelectedCountries,
        handleQuietTimeSubmitForAddNew,
        isReceiveNotifManuallyUnchecked, 
        setIsReceiveNotifManuallyUnchecked 
      );
      break;
    case 'RECIP':
      content = renderMobilePush(
        contactPath,
        onChange,
        contactPathError,
        setContactPathError,
        touched,
        setFieldTouched,
        index,
        pageServices,
        isNeedValidation,
        setIsNeedValidation,
        orderMemberPath,
        toggleTimeZoneModal,
        quietTimeTags,
        timeZone,
        useDeviceTimeFrame,
        handleQuietTimeSubmitForAddNew,
        isReceiveNotifManuallyUnchecked, 
        setIsReceiveNotifManuallyUnchecked 
      );
      break;
    case 'MSTEAMS':
    case 'EMAIL':
    default:
      content = renderTextField(
        contactPath,
        onChange,
        contactPathError,
        setContactPathError,
        touched,
        setFieldTouched,
        index,
        pageServices,
        isNeedValidation,
        setIsNeedValidation,
        orderMemberPath,
        toggleTimeZoneModal,
        quietTimeTags,
        timeZone,
        useDeviceTimeFrame,
        isAllFieldMandatory,
        handleQuietTimeSubmitForAddNew,
        isReceiveNotifManuallyUnchecked, 
        setIsReceiveNotifManuallyUnchecked 
      );
      break;
  }

  return (
    <div className="form-group row">
      {
        !timeZone && timeZoneModal
      }
      {orderMemberPath && (
        <div className="col-md-1">
          {isMobilePushLoggedIn(type, value) ?
            <ActionButtonsContainer className="action-buttons-container d-flex align-items-start">
              <Button
                className="sequence-handler-up p-0 border-0"
                disabled={inActiveUpButton}
                onClick={handleMoveUp}
                aria-label={i18n.get('button.priority.up.a11y.describe', contactPath.name)}
              >
                <span className="sr-only"><I18N>profile.contact.path.button.move.up</I18N></span>
                <FontAwesomeIcon
                  icon={faCaretSquareUp}
                  width="0.875em"
                  height="1em"
                />
              </Button>
              <Button
                className="sequence-handler-down p-0 border-0 ml-1"
                disabled={inActiveDownButton}
                onClick={handleMoveDown}
                aria-label={i18n.get('button.priority.down.a11y.describe', contactPath.name)}
              >
                <span className="sr-only"><I18N>profile.contact.path.button.move.down</I18N></span>
                <FontAwesomeIcon
                  icon={faCaretSquareDown}
                  width="0.875em"
                  height="1em"
                />
              </Button>
            </ActionButtonsContainer>
            : null
          }
        </div>
      )}
      {content}
    </div>
  );
};

ContactPathsRow.propTypes = {
  allContactPaths: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  contactPath: PropTypes.shape().isRequired,
  pageServices: PropTypes.arrayOf(PropTypes.shape()),
  // onChange: PropTypes.func.isRequired,
  handleMoveUp: PropTypes.func.isRequired,
  handleMoveDown: PropTypes.func.isRequired,
  errors: PropTypes.shape().isRequired,
  values: PropTypes.shape().isRequired,
  touched: PropTypes.shape().isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  contactPathError: PropTypes.shape().isRequired,
  setContactPathError: PropTypes.func.isRequired,
  setFieldError: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isNeedValidation: PropTypes.bool.isRequired,
  setIsNeedValidation: PropTypes.func.isRequired,
  orderMemberPath: PropTypes.bool,
  useDeviceTimeFrame: PropTypes.bool.isRequired,
  profileTimeZones: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isAllFieldMandatory: PropTypes.bool.isRequired,
  setIscallOutMsgShow: PropTypes.func.isRequired,
  contactPathSelectedCountries: PropTypes.shape().isRequired,
};
ContactPathsRow.defaultProps = {
  pageServices: [{}],
  orderMemberPath: false,
};
const mapStateToProps = state => ({
  pageServices: currentUserSelectors.selectPageServices(state),
  orderMemberPath: currentUserSelectors.selectOrderMemberPath(state),
  profileTimeZones: currentUserSelectors.selectTimezones(state),
  useDeviceTimeFrame: currentUserSelectors.selectIsUseDeviceTimeFrame(state),
  contactPathSelectedCountries: currentUserSelectors.selectContactPathSelectedCountries(state),
});

export default connect(mapStateToProps)(ContactPathsRow);
